import { Badge, Group, Stack, Text } from '@mantine/core'

import { Callout } from '@/components/ui'
import {
  enderecoDoWifiDoDono, provisionarMeuRobo, useMeuRobo, useWifiDoRobo, type Provisionamento, type Robo,
} from '@/api/robo'
import { ConfigurarPelaUsb } from '@/features/robo-usb/ConfigurarPelaUsb'
import { estadoDoRobo } from '../estado'
import { ModoRedeSemCabo, temModoRede } from '../ModoRedeSemCabo'
import { WifiDoRobo } from '../WifiDoRobo'

/**
 * Sem MAC não há como o backend conferir que a placa na USB é a deste robô
 * (409 `placa_de_outro_robo`/`placa_diverge`); o firmware que não informa MAC
 * é anterior à gravação pela Frota, e é por lá que ele se atualiza.
 */
function pedirCredencialDoDono(mac: string | null): Promise<Provisionamento> {
  if (!mac) {
    return Promise.reject(new Error(
      'Esta placa não informou o MAC. Peça a quem administra a frota para atualizar o firmware dela.'))
  }
  return provisionarMeuRobo(mac)
}

/**
 * Passo 2 (spec 2026-09-27, 4.4). O estado sai de `estadoDoRobo`, sem limiar
 * novo: ativo ou atrasado é "Conectado", com as redes pelo console; o resto
 * é a USB. Nunca bloqueia o pronto — quem monta o passo oferece "Faço isso depois".
 */
export function PassoRede({ robo, aoConfigurar }: { robo: Robo; aoConfigurar: () => void }) {
  const estado = estadoDoRobo(robo)
  const conectado = estado.chave === 'ativo' || estado.chave === 'atrasado'

  if (conectado) {
    return (
      <Stack gap="md">
        <Group gap="sm">
          <Badge color={estado.cor} variant="light">Conectado</Badge>
          <Text size="xs" c="dimmed">{estado.significado}</Text>
        </Group>
        <Text size="sm">Para ele usar outra rede, adicione-a pelo console:</Text>
        <WifiDoRobo robo={robo} endereco={enderecoDoWifiDoDono()} />
      </Stack>
    )
  }

  return <SemSinal robo={robo} aoConfigurar={aoConfigurar} />
}

/** Mesmo intervalo da Frota: a volta do robô aparece sem recarregar a página. */
const REPETIR_SEM_SINAL_MS = 30_000

function SemSinal({ robo, aoConfigurar }: { robo: Robo; aoConfigurar: () => void }) {
  useMeuRobo({ refetchInterval: REPETIR_SEM_SINAL_MS })
  const wifi = useWifiDoRobo(enderecoDoWifiDoDono())
  const semCabo = temModoRede(wifi.data?.relato)

  return (
    <Stack gap="md">
      {/* Já ter falado muda o que a pessoa conclui: sem sinal é rede que mudou
          (o robô foi entregue em outro lugar), não placa que nunca funcionou. */}
      <Callout
        variant="info"
        title={robo.last_seen_at
          ? `O robô está sem sinal desde ${new Date(robo.last_seen_at).toLocaleString('pt-BR')}`
          : 'O robô ainda não falou com o Console'}
      >
        {robo.last_seen_at && (semCabo
          ? 'Provavelmente ele não conhece a rede daqui: grave o Wi-Fi sem cabo ou pela USB. '
          : 'Provavelmente ele não conhece a rede daqui: grave o Wi-Fi pela USB. ')}
        {!semCabo && 'Use um cabo de dados: o cabo só de carga não aparece para o navegador.'}
      </Callout>
      {semCabo && <ModoRedeSemCabo />}
      {semCabo && (
        <Text size="sm">
          Pela USB, com um cabo de dados (o cabo só de carga não aparece para o navegador):
        </Text>
      )}
      <ConfigurarPelaUsb robo={robo} aoConcluir={aoConfigurar} pedirCredencial={pedirCredencialDoDono} />
    </Stack>
  )
}
